import { ManifestExtra } from 'expo-constants/build/Constants.types';
import * as Updates from 'expo-updates';
import { zip } from 'ramda';
import { useEffect } from 'react';
import {
  Alert,
  AppState,
  AppStateStatus,
  Linking,
  Platform,
} from 'react-native';
import { getNativeAppVersion } from '../../utils/platformUtils';
import { code } from '../../version.json';
import { remoteConfig } from '../firebase/firebaseAccess';
import { isVersionMatchingTarget } from '../ota-updates/otaUpdatesVersionTargeting';

type ApplyUpdate = 'immediately' | 'onBackground' | 'onNextBoot';

// keeps track of OTA updates logs aiding critical debugging
export let otaUpdatesLogs: string[] = [];

type Extra = {
  applyUpdate: ApplyUpdate;
  targetVersion?: string;
  immediateUntil?: number;
  commitHash: string;
  commitCount: number;
};

const log = (s: string) => {
  otaUpdatesLogs.unshift(s);
  if (otaUpdatesLogs.length > 20) {
    otaUpdatesLogs = otaUpdatesLogs.slice(0, 20);
  }
};

export const extraFromProperties = (props: {
  applyUpdate: any;
  targetVersion?: string;
  immediateUntil?: string;
  commitHash: string;
  commitCount: number;
}): Extra => {
  return {
    applyUpdate: props.applyUpdate ?? 'immediately',
    targetVersion: props.targetVersion,
    immediateUntil: props.immediateUntil
      ? Number(props.immediateUntil)
      : undefined,
    commitHash: props.commitHash,
    commitCount: props.commitCount,
  };
};

const getExtraFromManifest = (
  manifest: Updates.Manifest | null | undefined,
): Extra | undefined => {
  if (manifest == null || manifest.extra == null) {
    return undefined;
  }

  // Expo uses new manifest format starting from new eas build
  if ('expoClient' in manifest.extra) {
    const newExtra = manifest.extra as ManifestExtra;
    if (newExtra.expoClient != null && newExtra.expoClient.extra != null) {
      return extraFromProperties(newExtra.expoClient.extra as any);
    }
  }

  // fallback to old expo manifest format
  return extraFromProperties(manifest.extra as any);
};

const isUpdatesVersionTargetingMatched = (
  version: string,
  extra: Extra | null | undefined,
) => {
  return (
    extra?.targetVersion &&
    isVersionMatchingTarget(version, extra.targetVersion)
  );
};

const openStorePage = () => {
  void Linking.openURL(
    Platform.select({
      ios: 'https://apps.apple.com/us/app/popshop-start-shopping-us/id1009480270?ls=1',
      android: 'https://play.google.com/store/apps/details?id=com.popshoplive',
    })!,
  );
};

const checkForNativeUpdates = async (version: string) => {
  if (Platform.OS === 'web') {
    return;
  }

  const fetched = await remoteConfig.fetchAndActivate();
  if (!fetched) {
    return;
  }

  // this is the minimum supported native version, in case the native
  // version of the app installed on user's device is too low, we kindly
  // inform user and bring him to the app store page to upgrade.
  const minSupportedVersion = remoteConfig.getString('force_update_version');
  const needsUpdate =
    zip(
      version.split('.').map(Number),
      minSupportedVersion.split('.').map(Number),
    ).reduce((needUpdate, [local, min]) => {
      if (needUpdate !== undefined || local === min) {
        return needUpdate;
      }
      return min > local;
    }, undefined as boolean | undefined) ?? false;

  if (!needsUpdate) {
    return;
  }

  Alert.alert(
    'Update available',
    'An update is available, please update your app to latest version.',
    [
      {
        text: 'Update now',
        onPress: openStorePage,
      },
    ],
  );
};

export function useCheckForUpdates() {
  useEffect(() => {
    if (__DEV__ || Platform.OS === 'web') {
      return;
    }

    const version = getNativeAppVersion();

    // check if native binary force update is required
    void checkForNativeUpdates(version);

    let reloadOn: ApplyUpdate | undefined;
    let isAppActive = true;
    let isChecking = false;

    const checkForUpdate = async () => {
      if (isChecking) {
        return;
      }

      isChecking = true;

      try {
        const result = await Updates.checkForUpdateAsync();

        const checkExtra = getExtraFromManifest(result.manifest);

        log('check updates result: ' + JSON.stringify(checkExtra, null, 2));

        if (!result.isAvailable) {
          log('ota updates is unavailable');
          return;
        }

        // if this OTA updates is only appropriate for a given target version
        // then we must ensure the current app native version matches that
        // otherwise, we just skip this update.
        if (!isUpdatesVersionTargetingMatched(version, checkExtra)) {
          const targetVersion = checkExtra?.targetVersion;
          log(`version not matched (${targetVersion}), update skipped`);
          return;
        }

        const newUpdate = await Updates.fetchUpdateAsync();
        const fetchExtra = getExtraFromManifest(newUpdate.manifest);
        log('fetch update result: ' + JSON.stringify(fetchExtra, null, 2));

        if (!newUpdate.isNew) {
          log('updates downloaded but is not new, skipped');
          return;
        }

        if (fetchExtra == null) {
          return;
        }

        // double check the version targeting when updates have been downloaded
        if (!isUpdatesVersionTargetingMatched(version, fetchExtra)) {
          log(
            'downloaded version not matched, updates skipped ' +
              'but will be applied on next boot',
          );
          return;
        }

        const applyUpdate = fetchExtra.applyUpdate ?? 'immediately';
        const immediateUntil = fetchExtra.immediateUntil;

        if (
          applyUpdate === 'immediately' &&
          (immediateUntil == null || code < Number(immediateUntil))
        ) {
          await Updates.reloadAsync();
          return;
        }

        if (applyUpdate === 'onBackground' && !isAppActive) {
          await Updates.reloadAsync();
        }

        reloadOn = applyUpdate;
      } catch (err) {
        log('error check for updates: ' + (err as any)?.message);
        console.error(err);
      } finally {
        isChecking = false;
      }
    };

    const callback = async (status: AppStateStatus) => {
      isAppActive = status !== 'background';

      if (reloadOn === 'onBackground' && status === 'background') {
        await Updates.reloadAsync();
      } else if (status === 'active') {
        // check for updates when app goes to foreground again
        await checkForUpdate();
      }
    };

    checkForUpdate().catch(() => null);

    const sub = AppState.addEventListener('change', callback);

    return () => {
      return sub.remove();
    };
  }, []);
}
